import {fake} from '@fixtures/fake';
import {middlewareUserFixtureFactory} from '@fixtures/user';
import {GetTokenParams, type GetTokenHeaders, type OrganizationResponse, type UserResponse} from '@middleware';
import {AppEnvironment} from '@models/environment';

export function shouldUseDevUser(appEnvironment: AppEnvironment): boolean {
  return appEnvironment.userMode === 'dev';
}

export function shouldUseTestUser(appEnvironment: AppEnvironment): boolean {
  return appEnvironment.userMode === 'test';
}

export function isRealUser(appEnvironment: AppEnvironment): boolean {
  return appEnvironment.userMode === 'real';
}

/** Returns a GetTokenHeaders object containing the EPPN and display name for a dev user or a fake test user,
 * depending on whether the environment userMode is dev or test. */
export function applyDummyUserIfApplicable(
  appEnvironment: AppEnvironment,
  org: OrganizationResponse,
): {_user: UserResponse; _tokenParams: GetTokenParams; _tokenHeaders: GetTokenHeaders} {
  if (shouldUseTestUser(appEnvironment)) {
    // Randomly generate a fake user for testing purposes
    const email = `${fake.internet.username()}@${org.email_domains[0]}`;
    const user = middlewareUserFixtureFactory({email: email, organization: org});
    return {
      _user: user,
      _tokenParams: {organization: org.name},
      _tokenHeaders: {displayname: user.display_name, eppn: user.email},
    };
  } else if (shouldUseDevUser(appEnvironment)) {
    // Get dev user from the environment.local.ts file
    const devUser = appEnvironment.devUser;
    if (devUser) {
      return {
        _user: middlewareUserFixtureFactory({
          email: devUser.eppn,
          display_name: devUser.display_name,
          organization: org,
        }),
        _tokenParams: {organization: org.name},
        _tokenHeaders: {displayname: devUser.display_name, eppn: devUser.eppn},
      };
    } else {
      console.warn(`No dev user configured!`);
    }
  }

  // "real" user mode or no dev user found
  return {
    _user: null,
    _tokenParams: {organization: org.name},
    _tokenHeaders: {displayname: null, eppn: 'no_eppn'},
  };
}

// Modifies the given loginParams and loginHeaders to use a dummy user if applicable.
export function useDummyUser(
  appEnvironment: AppEnvironment,
  org: OrganizationResponse,
  loginParams: GetTokenParams,
  loginHeaders: GetTokenHeaders,
) {
  const {_tokenParams, _tokenHeaders} = applyDummyUserIfApplicable(appEnvironment, org);
  loginParams.organization = _tokenParams.organization;
  loginHeaders.eppn = _tokenHeaders.eppn;
  loginHeaders['displayname'] = _tokenHeaders['displayname'];
}
